/**
 * Commercial Hub — P4.6A Commercial Cycle Closure
 *
 * Single-page command center for the *cash* side of the contracting
 * business. Lives parallel to ProjectControlHub but pivots around
 * customer billing instead of project execution.
 *
 * Tabs:
 *   1. Receivables (AR Aging)     — who owes us, how aged
 *   2. Cash Forecast              — money in / out over next N weeks
 *   3. Receipts                   — issued receipt vouchers + draft posting
 *   4. Invoices from Claims       — generate INV-XXXX from approved claims
 *
 * No new accounting endpoints invoked here — this page is a pure
 * read+orchestrate UI over the existing P4.6A APIs.
 */
import React, { useEffect, useState } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Banknote, Receipt as ReceiptIcon, TrendingDown, TrendingUp, Wallet,
  Calendar, FilePlus2, AlertCircle, CheckCircle2, Loader2,
  ArrowDownToLine, ArrowUpFromLine,
} from "lucide-react";

const SAR = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "SAR", maximumFractionDigits: 0,
  }).format(Number(n || 0));

const Tab = ({ id, active, onClick, icon: Icon, children, testid }) => (
  <button
    data-testid={testid}
    onClick={() => onClick(id)}
    className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all ${
      active === id
        ? "border-slate-900 bg-slate-900 text-white shadow-sm"
        : "border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900"
    }`}
  >
    {Icon && <Icon className="h-4 w-4" />}
    {children}
  </button>
);

const KPI = ({ label, value, sub, tone = "slate", icon: Icon, testid }) => {
  const tones = {
    slate:  "from-slate-50 to-white border-slate-200",
    blue:   "from-blue-50 to-white border-blue-200",
    amber:  "from-amber-50 to-white border-amber-200",
    green:  "from-emerald-50 to-white border-emerald-200",
    red:    "from-rose-50 to-white border-rose-200",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border bg-gradient-to-br p-5 ${tones[tone]}`}
    >
      <div className="flex items-start justify-between">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
          {label}
        </div>
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
};

// ─── AR Aging panel ───────────────────────────────────────────────

const ArAgingPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get("/reports/ar-aging");
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) toast.error("فشل تحميل تقرير أعمار الذمم");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…
    </div>
  );
  if (!data) return null;

  const t = data.totals || {};
  return (
    <div data-testid="commercial-ar-aging" className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KPI label="Total Outstanding" value={SAR(t.total_outstanding)}
              tone="slate" icon={Wallet} testid="ar-total"
              sub={`${t.invoice_count || 0} invoices`} />
        <KPI label="Current (0-30)" value={SAR(t.current)} tone="green"
              icon={CheckCircle2} testid="ar-current" />
        <KPI label="31-60 days" value={SAR(t["31-60"])} tone="amber"
              testid="ar-31-60" />
        <KPI label="61-90 days" value={SAR(t["61-90"])} tone="amber"
              testid="ar-61-90" />
        <KPI label="90+ days" value={SAR(t["90+"])} tone="red"
              icon={AlertCircle} testid="ar-90-plus" />
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">
          أعمار الذمم حسب العميل
          <span className="ml-2 text-xs font-normal text-slate-500">
            as of {data.as_of}
          </span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Customer</th>
              <th className="px-4 py-2 text-right">Current</th>
              <th className="px-4 py-2 text-right">31-60</th>
              <th className="px-4 py-2 text-right">61-90</th>
              <th className="px-4 py-2 text-right">90+</th>
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2 text-right">Oldest</th>
            </tr>
          </thead>
          <tbody data-testid="ar-aging-rows">
            {(data.buckets || []).map((r, i) => (
              <tr key={r.customer_id || i}
                  className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-medium text-slate-900">
                  {r.customer_name}
                </td>
                <td className="px-4 py-2 text-right text-emerald-700">{SAR(r.current)}</td>
                <td className="px-4 py-2 text-right text-amber-700">{SAR(r["31-60"])}</td>
                <td className="px-4 py-2 text-right text-amber-700">{SAR(r["61-90"])}</td>
                <td className="px-4 py-2 text-right text-rose-700">{SAR(r["90+"])}</td>
                <td className="px-4 py-2 text-right font-semibold">{SAR(r.total_outstanding)}</td>
                <td className="px-4 py-2 text-right text-slate-500">{r.oldest_invoice_days}d</td>
              </tr>
            ))}
            {(data.buckets || []).length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                لا توجد ذمم مستحقة حالياً
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Cash Forecast panel ──────────────────────────────────────────

const CashForecastPanel = () => {
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(12);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get(`/reports/cash-forecast?weeks=${weeks}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) toast.error("فشل تحميل التدفق النقدي");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [weeks]);

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…
    </div>
  );
  if (!data) return null;
  const t = data.totals || {};
  const maxFlow = Math.max(
    ...(data.weeks || []).map((w) => Math.max(w.cash_in || 0, w.cash_out || 0)),
    1,
  );

  return (
    <div data-testid="commercial-cash-forecast" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-3 gap-4 flex-1">
          <KPI label={`Cash In (${weeks}w)`} value={SAR(t.total_cash_in)}
                tone="green" icon={ArrowDownToLine} testid="cash-in-total" />
          <KPI label={`Cash Out (${weeks}w)`} value={SAR(t.total_cash_out)}
                tone="red" icon={ArrowUpFromLine} testid="cash-out-total" />
          <KPI label="Net Position" value={SAR(t.net_position)}
                tone={t.net_position >= 0 ? "green" : "red"}
                icon={t.net_position >= 0 ? TrendingUp : TrendingDown}
                testid="cash-net-position" />
        </div>
        <div className="ml-4 flex items-center gap-2">
          <span className="text-xs text-slate-500">Horizon:</span>
          {[4, 8, 12, 26].map((w) => (
            <button key={w}
              data-testid={`weeks-${w}`}
              onClick={() => setWeeks(w)}
              className={`rounded-md px-3 py-1 text-xs font-medium ${
                weeks === w
                  ? "bg-slate-900 text-white"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}>{w}w</button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-sm font-semibold text-slate-700">
          التدفق النقدي الأسبوعي
        </div>
        <div className="space-y-2" data-testid="cash-forecast-rows">
          {(data.weeks || []).map((w) => (
            <div key={w.week_index}
                 data-testid={`cash-forecast-row-${w.week_index}`}
                 className="grid grid-cols-12 items-center gap-3 text-xs">
              <div className="col-span-1 font-mono text-slate-500">{w.label}</div>
              <div className="col-span-2 text-slate-400">{w.start_date}</div>
              <div className="col-span-7 flex h-5 items-center gap-1">
                <div className="h-3 rounded-r bg-emerald-400"
                      style={{ width: `${(w.cash_in / maxFlow) * 50}%` }} />
                <div className="h-3 rounded-l bg-rose-400"
                      style={{ width: `${(w.cash_out / maxFlow) * 50}%` }} />
              </div>
              <div className="col-span-1 text-right font-semibold text-emerald-700">
                {SAR(w.cash_in)}
              </div>
              <div className="col-span-1 text-right font-semibold text-rose-700">
                {SAR(w.cash_out)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Receipts panel ───────────────────────────────────────────────

const ReceiptsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get("/receipt-vouchers?limit=50");
        if (alive) setRows(r.data || []);
      } catch (e) {
        if (alive) toast.error("فشل تحميل سندات القبض");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const handlePost = async (id) => {
    setPosting(id);
    try {
      const r = await axiosInstance.post(`/receipt-vouchers/${id}/post`);
      toast.success(`تم الترحيل — Journal ${r.data.journal_no}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل ترحيل سند القبض");
    } finally {
      setPosting(null);
    }
  };

  const statusPill = (s) => {
    const map = {
      draft:     "bg-slate-100 text-slate-700 border-slate-200",
      posted:    "bg-emerald-50 text-emerald-700 border-emerald-200",
      cancelled: "bg-rose-50 text-rose-700 border-rose-200",
    };
    return (
      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${map[s] || map.draft}`}>
        {s}
      </span>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…
    </div>
  );

  return (
    <div data-testid="commercial-receipts" className="space-y-4">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">
          سندات القبض ({rows.length})
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">RV #</th>
              <th className="px-4 py-2 text-left">Customer</th>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Method</th>
              <th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Journal</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="receipts-rows">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono text-slate-700">{r.receipt_number}</td>
                <td className="px-4 py-2">{r.customer_name || "—"}</td>
                <td className="px-4 py-2 text-slate-500">{r.receipt_date}</td>
                <td className="px-4 py-2 text-slate-500">{r.payment_method}</td>
                <td className="px-4 py-2 text-right font-semibold">{SAR(r.amount)}</td>
                <td className="px-4 py-2">{statusPill(r.status)}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-500">
                  {r.journal_no || "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  {r.status === "draft" && (
                    <button
                      data-testid={`post-receipt-${r.id}`}
                      disabled={posting === r.id}
                      onClick={() => handlePost(r.id)}
                      className="rounded-md bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50">
                      {posting === r.id ? "..." : "Post"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                لا توجد سندات قبض حالياً. أنشئ سند قبض من تبويب الفواتير.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Invoices-from-Claims panel ───────────────────────────────────

const ClaimsToInvoicePanel = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get("/progress-claims");
        if (alive) setClaims((r.data || []).filter(
          (c) => c.status === "approved" && c.claim_type === "client",
        ));
      } catch (e) {
        if (alive) toast.error("فشل تحميل المستخلصات");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const issue = async (claim_id) => {
    setIssuing(claim_id);
    try {
      const r = await axiosInstance.post(
        `/customer-invoices/from-claim/${claim_id}`,
      );
      toast.success(`تم إصدار الفاتورة ${r.data.invoice_number}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إصدار الفاتورة");
    } finally {
      setIssuing(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…
    </div>
  );

  return (
    <div data-testid="commercial-claims-to-invoice" className="space-y-4">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <strong>P4.6A — Commercial Cycle</strong>
        <p className="mt-1 text-blue-700">
          المستخلصات المعتمدة أدناه يمكن تحويلها إلى فواتير عملاء رسمية.
          القيد المحاسبي لـ AR موجود مسبقاً من اعتماد المستخلص — لذا لا يتم
          إنشاء قيد جديد عند إصدار الفاتورة (Document Wrapper فقط).
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">
          المستخلصات المعتمدة الجاهزة للتفويض
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Claim #</th>
              <th className="px-4 py-2 text-left">Project</th>
              <th className="px-4 py-2 text-right">Gross</th>
              <th className="px-4 py-2 text-right">Retention</th>
              <th className="px-4 py-2 text-right">VAT</th>
              <th className="px-4 py-2 text-right">Payable</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="claims-rows">
            {claims.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono">{c.claim_number}</td>
                <td className="px-4 py-2">{c.project_name || "—"}</td>
                <td className="px-4 py-2 text-right">{SAR(c.current_gross)}</td>
                <td className="px-4 py-2 text-right text-amber-700">
                  {SAR(c.retention_amount)}
                </td>
                <td className="px-4 py-2 text-right">{SAR(c.vat_amount)}</td>
                <td className="px-4 py-2 text-right font-semibold">
                  {SAR(c.total_payable)}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    data-testid={`issue-invoice-${c.id}`}
                    disabled={issuing === c.id}
                    onClick={() => issue(c.id)}
                    className="rounded-md bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50">
                    {issuing === c.id ? "..." : "Issue Invoice"}
                  </button>
                </td>
              </tr>
            ))}
            {claims.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                لا توجد مستخلصات معتمدة جاهزة للفوترة.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────

export default function CommercialHub() {
  const [tab, setTab] = useState("aging");

  return (
    <div data-testid="commercial-hub" className="space-y-6 p-6" dir="rtl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            <Banknote className="inline -mt-1 mr-2 h-7 w-7 text-emerald-600" />
            Commercial Hub
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            دورة التحصيل النقدي الكاملة — Receivables · Cash · Receipts · Invoicing
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" dir="ltr">
        <Tab id="aging" active={tab} onClick={setTab} icon={Wallet}
              testid="tab-aging">AR Aging</Tab>
        <Tab id="forecast" active={tab} onClick={setTab} icon={Calendar}
              testid="tab-forecast">Cash Forecast</Tab>
        <Tab id="receipts" active={tab} onClick={setTab} icon={ReceiptIcon}
              testid="tab-receipts">Receipts</Tab>
        <Tab id="claims" active={tab} onClick={setTab} icon={FilePlus2}
              testid="tab-claims">Claims → Invoices</Tab>
      </div>

      <div dir="ltr">
        {tab === "aging"    && <ArAgingPanel />}
        {tab === "forecast" && <CashForecastPanel />}
        {tab === "receipts" && <ReceiptsPanel />}
        {tab === "claims"   && <ClaimsToInvoicePanel />}
      </div>
    </div>
  );
}
